import { useState, useEffect, useCallback, RefObject } from 'react';

interface UseFullscreenProps {
	playerRef: RefObject<HTMLElement>;
}

export function useFullscreen({ playerRef }: Readonly<UseFullscreenProps>) {
	const [fullscreen, setFullscreen] = useState<boolean>(false);

	const handleEnterFullscreen = useCallback(async () => {
		await playerRef.current?.requestFullscreen();
	}, [playerRef]);

	const handleExitFullscreen = useCallback(async () => {
		await document.exitFullscreen();
	}, []);

	const toggleFullscreen = useCallback(async () => {
		if (!playerRef.current) return;

		if (!document.fullscreenElement) {
			await handleEnterFullscreen();
			return;
		}

		await handleExitFullscreen();
	}, [handleEnterFullscreen, handleExitFullscreen, playerRef]);

	useEffect(() => {
		const handleFullscreenChange = () => {
			setFullscreen(!!document.fullscreenElement);
		};

		document.addEventListener('fullscreenchange', handleFullscreenChange);
		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
		};
	}, []);

	return {
		fullscreen,
		toggleFullscreen
	};
}
